import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSync } from "@/context/SyncContext";
import { Logo } from "@/components/Logo";
import { LegalDisclaimer } from "@/context/DisclaimerContext";
import { FeedbackModal } from "@/context/FeedbackContext";
import { BottomNav } from "@/components/BottomNav";
import { HowItWorks } from "@/components/HowItWorks";
import { SyncBar } from "@/components/SyncBar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LayoutGrid, Settings, LogOut, PlusCircle, MessageSquare, LifeBuoy } from "lucide-react";

export function AppShell({ children, wide = false }) {
  const { user } = useAuth();
  const { requestLogout } = useSync();
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const container = wide
    ? "max-w-6xl lg:max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8"
    : "max-w-6xl mx-auto px-4 sm:px-6";

  const doLogout = () => requestLogout(() => navigate("/"));

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      <header className="sticky top-0 z-30 border-b border-[#E2E8F0] bg-white/80 backdrop-blur-xl">
        <div className={`${container} h-16 flex items-center justify-between`}>
          <button onClick={() => navigate("/dashboard")} data-testid="header-logo-btn">
            <Logo />
          </button>
          <div className="flex items-center gap-2">
            <nav className="hidden md:flex items-center gap-1 mr-2">
              <button data-testid="header-nav-properties" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors">
                <LayoutGrid size={17} /> Properties
              </button>
              <HowItWorks />
              <button data-testid="header-nav-start" onClick={() => navigate("/start")} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors">
                <PlusCircle size={17} /> New
              </button>
            </nav>
            <button data-testid="header-help-btn" onClick={() => navigate("/help")} aria-label="Help Center" className="grid place-items-center h-9 w-9 rounded-full text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors">
              <LifeBuoy size={18} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="rounded-full ring-2 ring-transparent hover:ring-[#E2E8F0] transition-all">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-navy text-white text-sm font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{user?.name}</p>
                  <p className="text-xs text-[#94A3B8] truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>
                  <Settings size={16} className="mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-help" onClick={() => navigate("/help")}>
                  <LifeBuoy size={16} className="mr-2" /> Help Center
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-feedback" onClick={() => setFeedbackOpen(true)}>
                  <MessageSquare size={16} className="mr-2" /> Send feedback
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={doLogout} className="text-[#B91C1C] focus:text-[#B91C1C]">
                  <LogOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <SyncBar />
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <main className={`${container} py-6 pb-28 md:pb-12`}>{children}</main>
      <footer className="border-t border-[#E2E8F0] bg-white/60" data-testid="app-footer">
        <div className={`${container} py-6 text-center space-y-2`}>
          <LegalDisclaimer keyName="general_footer" className="text-xs text-[#94A3B8]" testId="footer-disclaimer" />
          <div className="flex items-center justify-center gap-4">
            <a href="/help" data-testid="footer-help-link" className="text-xs text-[#475569] hover:text-brand">Help Center</a>
            <a href="/privacy" data-testid="footer-privacy-link" className="text-xs text-[#475569] hover:text-brand">Privacy</a>
            <a href="/terms" data-testid="footer-terms-link" className="text-xs text-[#475569] hover:text-brand">Terms</a>
          </div>
        </div>
      </footer>
      <BottomNav />
    </div>
  );
}
